import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { mangaArticles } from '@/mocks/mangaArticles';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const article = mangaArticles.find((m) => m.id === id);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">作品未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">您访问的漫画作品不存在或已下架</p>
          <Link
            to="/category"
            className="inline-block bg-primary-500 text-background-50 dark:text-foreground-950 px-6 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            返回分类列表
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = mangaArticles
    .filter((m) => m.id !== article.id && m.category === article.category)
    .slice(0, 4);

  const articleContent = article.content || `
    <h2>作品简介</h2>
    <p>${article.description}</p>
    <h3>故事背景</h3>
    <p>在这个充满奇幻色彩的世界中，主人公踏上了一段不平凡的旅程。随着故事的推进，一个宏大的世界观逐渐展开，各种精彩纷呈的冒险故事接踵而至。</p>
    <h3>角色介绍</h3>
    <p>本作拥有丰富立体的角色群像，每个角色都有自己独特的背景故事和成长轨迹，让读者在阅读过程中产生强烈的情感共鸣。</p>
    <h3>精彩看点</h3>
    <p>精良的画面制作、扣人心弦的剧情发展、深刻的人物刻画，使得这部作品在众多漫画中脱颖而出，成为广大读者追捧的热门之作。</p>
  `;

  return (
    <>
      <SEOHead
        title={`${article.title} - 禁漫天堂 | ${article.tags.join('·')}`}
        description={article.description}
        keywords={`${article.title},${article.tags.join(',')},禁漫天堂漫画`}
        canonicalUrl={`https://jmtt.org/article/${article.id}`}
        ogType="article"
        schemaJsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.description,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.chapters[0]?.updateTime,
            dateModified: article.updateTime,
            url: `https://jmtt.org/article/${article.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jmtt.org/' },
              { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://jmtt.org/category' },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://jmtt.org/article/${article.id}` },
            ],
          },
        ]}
      />

      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: '漫画分类', path: '/category' },
          { label: article.title },
        ]}
      />

      <main className="w-full pb-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <article className="bg-background-50">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="rounded-xl overflow-hidden sticky top-24">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-flag-line"></i> {article.status}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i> {article.rating}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {(article.views / 10000).toFixed(0)}万阅读
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-secondary-100 text-secondary-800 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed mb-8"
                  dangerouslySetInnerHTML={{ __html: articleContent }}
                />

                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-list-check"></i> 章节目录
                  </h3>
                  <div className="space-y-2">
                    {article.chapters.map((chapter, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        <span className="text-sm text-foreground-800">{chapter.title}</span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{chapter.updateTime}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-16">
              <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedArticles.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/article/${manga.id}`}
                    className="group"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                      <img
                        src={manga.coverImage}
                        alt={manga.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                      {manga.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-1">评分 {manga.rating}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}